/**
 * Что показывать в строке-событии вызова инструмента (макет 8-2).
 *
 * Подпись в макете разная у разных инструментов: «1 задача · 0,4 с» у поиска,
 * «DEV-1478 · 0,6 с» у чтения задачи. Таблицы «инструмент → как подписать»
 * здесь нет намеренно: инструментов четырнадцать, каждый новый требовал бы
 * правки этого файла, а забытый — показывал бы голое имя. Вместо неё два общих
 * правила по форме ответа, которые и дают обе подписи из макета.
 */

/** Подпись строки: либо счёт найденного, либо то, с чем инструмент работал. */
export type CallSummary =
  | { kind: 'count'; of: 'issues' | 'pages'; value: number }
  | { kind: 'subject'; value: string }
  | null

/** Аргументы, в которых лежит ключ единственной сущности вызова. */
//
// `issue` и `id` идут последними: их носят инструменты базы знаний (link_page,
// update_page, list_issue_pages), и без них строка вызова остаётся безымянной.
const KEY_ARGS = ['key', 'issue_key', 'epic_key', 'project_key', 'page_id', 'issue', 'id']

const asRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null

const asKey = (value: unknown): string => (typeof value === 'string' && value ? value : '')

/**
 * Подпись вызова по его аргументам и ответу.
 *
 * Счёт берётся из `total` — его отдают все выдачи реестра; существительное — по
 * тому, какой список рядом. Если считать нечего, показывается ключ: сначала из
 * ответа (создание задачи знает ключ только там), потом из аргументов.
 */
export const summarizeCall = (args: unknown, result: unknown): CallSummary => {
  const out = asRecord(result)

  if (out) {
    if (typeof out.total === 'number') {
      return { kind: 'count', of: Array.isArray(out.pages) ? 'pages' : 'issues', value: out.total }
    }

    // У страницы подписью служит заголовок, а не идентификатор: uuid в строке
    // событий не говорит человеку ничего.
    const key =
      asKey(out.key) ||
      asKey(asRecord(out.issue)?.key) ||
      asKey(asRecord(out.page)?.title) ||
      asKey(asRecord(out.page)?.id)

    if (key) return { kind: 'subject', value: key }
  }

  const input = asRecord(args)

  if (input) {
    for (const name of KEY_ARGS) {
      const key = asKey(input[name])

      if (key) return { kind: 'subject', value: key }
    }
  }

  return null
}

/**
 * Длительность вызова — «0,4 с».
 *
 * Через `Intl`, а не через `toFixed`: в русской локали разделитель запятая, и
 * подставлять её руками значит писать «0,4 с» и во вьетнамской.
 */
export const formatDuration = (ms: number, locale: string): string =>
  new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(ms / 1000)

/** Предел показа аргументов и ответа под шевроном. */
export const DETAILS_LIMIT = 2000

/**
 * Аргументы и ответ в читаемом виде.
 *
 * Обрезка обязательна: выдача поиска — это сто задач целиком, и развёрнутая
 * строка вешала бы панель на самом обычном вопросе.
 */
export const formatDetails = (value: unknown): string => {
  if (value === undefined || value === null) return ''

  let text = ''

  try {
    text = JSON.stringify(value, null, 2) ?? ''
  } catch {
    // Циклическую ссылку в JSON с провода не пришлют, но падать из-за показа
    // подробностей нельзя — строка события важнее их.
    return ''
  }

  return text.length > DETAILS_LIMIT ? `${text.slice(0, DETAILS_LIMIT)}…` : text
}
